import React, { useEffect, useState, useCallback } from "react";
import Layout from "../layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import { totalTransactions } from "../Redux/Action/TransactionAction";
import Pagination from "../Component/Pagination";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import Api from "../Config/ApiConfig";
import { toast } from "react-toastify";
import { Modal, Button, Form, Spinner, Badge } from "react-bootstrap";

export default function WithDraw({ defaultTab = "requests" }) {
  const limit = 10;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Determine active tab from URL query or location pathname
  const queryParams = new URLSearchParams(location.search);
  const urlTab = queryParams.get("tab");
  const initialTab =
    urlTab === "history" || urlTab === "requests"
      ? urlTab
      : location.pathname === "/carer-withdrawals"
      ? "requests"
      : defaultTab;

  const [activeTab, setActiveTab] = useState(initialTab);

  // ==========================================
  // TAB 1: CARER WITHDRAWAL REQUESTS STATE
  // ==========================================
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    pendingCount: 0,
    completedCount: 0,
    rejectedCount: 0,
    totalPendingAmount: 0,
    totalCompletedAmount: 0,
  });

  // Modal States
  const [selectedItem, setSelectedItem] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Approve options: "stripe" or "manual"
  const [payoutMethod, setPayoutMethod] = useState("stripe");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [copied, setCopied] = useState(false);

  // Reject options
  const [rejectionReason, setRejectionReason] = useState("");

  // Stripe Environment state
  const [stripeEnv, setStripeEnv] = useState({ is_sandbox: true, mode: "sandbox" });

  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Api.get("/wallet-withdrawals", {
        params: {
          page,
          limit,
          status: statusFilter === "all" ? "" : statusFilter,
          search: searchTerm.trim(),
        },
      });

      if (response?.data?.status_code === 200 || response?.data?.data) {
        const payload = response.data.data;
        setWithdrawals(payload.withdrawals || []);
        setTotalCount(payload.count || 0);
        if (payload.stats) {
          setStats(payload.stats);
        }
        if (payload.stripe_environment) {
          setStripeEnv(payload.stripe_environment);
        }
      } else {
        toast.error(response?.data?.message || "Failed to fetch withdrawals");
      }
    } catch (err) {
      console.error("Error fetching withdrawals:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to fetch withdrawals");
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, searchTerm]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  useEffect(() => {
    Api.get("/stripe-environment")
      .then((res) => {
        if (res?.data?.data) {
          setStripeEnv(res.data.data);
        }
      })
      .catch(() => {});
  }, []);

  // ==========================================
  // TAB 2: ORIGINAL WITHDRAWAL TRANSACTIONS LOG
  // ==========================================
  const [historyPage, setHistoryPage] = useState(1);
  useEffect(() => {
    dispatch(totalTransactions({ limit, page: historyPage, status: ["withdraw"] }));
  }, [dispatch, limit, historyPage]);

  const list = useSelector((state) => state.Transaction.total_transaction);

  // Pagination Handlers
  const handlePageClickRequests = (e) => {
    setPage(e.selected + 1);
  };

  const handlePageClickHistory = (e) => {
    setHistoryPage(e.selected + 1);
  };

  // Open modals
  const handleOpenApprove = (item) => {
    setSelectedItem(item);
    setPayoutMethod(item?.user?.bank_details?.account_number ? "stripe" : "manual");
    setReferenceNumber("");
    setCopied(false);
    setShowApproveModal(true);
  };

  const handleOpenReject = (item) => {
    setSelectedItem(item);
    setRejectionReason("");
    setShowRejectModal(true);
  };

  const handleOpenDetails = (item) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  };

  const handleCopyBankDetails = () => {
    if (!selectedItem?.user?.bank_details) return;
    const b = selectedItem.user.bank_details;
    const text = [
      `Bank Name: ${b.bank_name || "N/A"}`,
      `Account Holder: ${b.account_holder_name || "N/A"}`,
      `Account Number: ${b.account_number || "N/A"}`,
      `Routing / Sort Code / IFSC: ${b.routing_number || "N/A"}`,
      `SWIFT / BIC: ${b.swift_code || "N/A"}`,
      `Country: ${b.country || "N/A"}`,
      `Withdrawal Amount: $${Number(selectedItem.amount || 0).toFixed(2)} ${(selectedItem.currency || "USD").toUpperCase()}`,
    ].join("\n");

    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.info("Bank details copied to clipboard!");
    setTimeout(() => setCopied(false), 2500);
  };

  // Execute Approval
  const handleConfirmApprove = async () => {
    if (!selectedItem) return;
    if (payoutMethod === "manual" && !referenceNumber.trim()) {
      toast.warn("Please enter a Bank UTR / Transaction Reference Number for manual transfer.");
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        payout_method: payoutMethod,
        reference_number: referenceNumber.trim(),
      };

      const response = await Api.post(`/wallet-withdrawals/${selectedItem._id}/approve`, payload);
      if (response?.data?.status_code === 200 || response?.data?.data) {
        toast.success(response?.data?.message || "Withdrawal approved and processed successfully!");
        setShowApproveModal(false);
        fetchWithdrawals();
        dispatch(totalTransactions({ limit, page: historyPage, status: ["withdraw"] }));
      } else {
        toast.error(response?.data?.message || "Failed to approve withdrawal");
      }
    } catch (err) {
      console.error("Approval error:", err);
      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Payment gateway failed to process withdrawal"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Execute Rejection
  const handleConfirmReject = async () => {
    if (!selectedItem) return;
    if (!rejectionReason.trim()) {
      toast.warn("Please enter a reason for rejection");
      return;
    }

    setActionLoading(true);
    try {
      const response = await Api.post(`/wallet-withdrawals/${selectedItem._id}/reject`, {
        reason: rejectionReason.trim(),
      });
      if (response?.data?.status_code === 200 || response?.data?.data) {
        toast.success("Withdrawal request rejected successfully");
        setShowRejectModal(false);
        fetchWithdrawals();
      } else {
        toast.error(response?.data?.message || "Failed to reject withdrawal");
      }
    } catch (err) {
      console.error("Rejection error:", err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to reject withdrawal");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Layout>
      <div className="dashboard_data_wrap">
        <div className="dashbord_cont_wrapper">
          {/* ==================================================== */}
          {/* ORIGINAL TOP METRIC HEADER CARDS                     */}
          {/* ==================================================== */}
          <div className="dashboard-header-card transcationcard">
            <div className="dashboard-content">
              <div className="dashboard-metrics">
                {/* Total Transaction Metric */}
                <div
                  className="metric transcationbxc"
                  onClick={() => {
                    navigate("/transaction");
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className="bxcontent">
                    <svg
                      width="54"
                      height="54"
                      viewBox="0 0 54 54"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M25.0875 41.8499H28.8563V39.2062C30.6938 38.9062 32.2125 38.1187 33.4125 36.8437C34.6125 35.5687 35.2125 34.0874 35.2125 32.3999C35.2125 30.7124 34.6969 29.2687 33.6656 28.0687C32.6344 26.8687 31.05 25.8749 28.9125 25.0874C26.625 24.2624 25.1157 23.5593 24.3844 22.978C23.6531 22.3968 23.2875 21.6374 23.2875 20.6999C23.2875 19.9499 23.6156 19.3312 24.2719 18.8437C24.9281 18.3562 25.7813 18.1124 26.8313 18.1124C27.8813 18.1124 28.8 18.3655 29.5875 18.8718C30.375 19.378 30.9 20.0624 31.1625 20.9249L34.4813 19.5187C34.0313 18.2812 33.3094 17.2405 32.3157 16.3968C31.3219 15.553 30.1688 15.0187 28.8563 14.7937V12.1499H25.1438V14.8499C23.4563 15.2249 22.1063 15.9468 21.0938 17.0155C20.0813 18.0843 19.575 19.3312 19.575 20.7562C19.575 22.4437 20.1469 23.878 21.2906 25.0593C22.4344 26.2405 24.2438 27.2624 26.7188 28.1249C28.4063 28.7249 29.6344 29.3624 30.4032 30.0374C31.1719 30.7124 31.5563 31.4999 31.5563 32.3999C31.5563 33.3749 31.1625 34.1905 30.375 34.8468C29.5875 35.503 28.6125 35.8312 27.45 35.8312C26.2125 35.8312 25.1157 35.4187 24.1594 34.5937C23.2031 33.7687 22.5563 32.6999 22.2188 31.3874L18.7313 32.8499C19.1813 34.4249 19.9688 35.7655 21.0938 36.8718C22.2188 37.978 23.55 38.7374 25.0875 39.1499V41.8499ZM27 48.5999C24.0375 48.5999 21.2438 48.0374 18.6188 46.9124C15.9938 45.7874 13.6969 44.2405 11.7281 42.2718C9.7594 40.303 8.21252 38.0062 7.08752 35.3812C5.96252 32.7562 5.40002 29.9624 5.40002 26.9999C5.40002 23.9999 5.96252 21.1968 7.08752 18.5905C8.21252 15.9843 9.7594 13.6968 11.7281 11.728C13.6969 9.75928 15.9938 8.2124 18.6188 7.0874C21.2438 5.9624 24.0375 5.3999 27 5.3999C30 5.3999 32.8032 5.9624 35.4094 7.0874C38.0157 8.2124 40.3032 9.75928 42.2719 11.728C44.2407 13.6968 45.7875 15.9843 46.9125 18.5905C48.0375 21.1968 48.6 23.9999 48.6 26.9999C48.6 29.9624 48.0375 32.7562 46.9125 35.3812C45.7875 38.0062 44.2407 40.303 42.2719 42.2718C40.3032 44.2405 38.0157 45.7874 35.4094 46.9124C32.8032 48.0374 30 48.5999 27 48.5999ZM27 44.5499C31.875 44.5499 36.0188 42.8437 39.4313 39.4312C42.8438 36.0187 44.55 31.8749 44.55 26.9999C44.55 22.1249 42.8438 17.9812 39.4313 14.5687C36.0188 11.1562 31.875 9.4499 27 9.4499C22.125 9.4499 17.9813 11.1562 14.5688 14.5687C11.1563 17.9812 9.45002 22.1249 9.45002 26.9999C9.45002 31.8749 11.1563 36.0187 14.5688 39.4312C17.9813 42.8437 22.125 44.5499 27 44.5499Z"
                        fill="#17C470"
                      />
                    </svg>
                    <div className="bcxcontent">
                      <h4>{list?.total || 0}</h4>
                      <small>Total transaction</small>
                    </div>
                  </div>
                </div>

                {/* Withdrawal Transaction Metric */}
                <div
                  className="metric transcationbxc"
                  onClick={() => {
                    setActiveTab("history");
                  }}
                  style={{
                    cursor: "pointer",
                    outline: activeTab === "history" ? "2px solid #FFC600" : "none",
                  }}
                >
                  <div className="bxcontent">
                    <svg
                      width="54"
                      height="54"
                      viewBox="0 0 54 54"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M24.75 49.3873C21.9 49.0873 19.2469 48.3092 16.7906 47.0529C14.3344 45.7967 12.1969 44.1654 10.3781 42.1592C8.55937 40.1529 7.125 37.8561 6.075 35.2686C5.025 32.6811 4.5 29.9248 4.5 26.9998C4.5 23.5873 5.18437 20.4373 6.55312 17.5498C7.92188 14.6623 9.7875 12.1873 12.15 10.1248H6.75V5.6248H20.25V19.1248H15.75V12.9936C13.6875 14.6436 12.0469 16.6779 10.8281 19.0967C9.60938 21.5154 9 24.1498 9 26.9998C9 31.6123 10.5094 35.5967 13.5281 38.9529C16.5469 42.3092 20.2875 44.2686 24.75 44.8311V49.3873ZM23.7937 37.3498L14.2875 27.7873L17.4375 24.6373L23.7937 30.9936L36.5625 18.2248L39.7125 21.4311L23.7937 37.3498ZM33.75 48.3748V34.8748H38.25V41.0061C40.3125 39.3186 41.9531 37.2748 43.1719 34.8748C44.3906 32.4748 45 29.8498 45 26.9998C45 22.3873 43.4906 18.4029 40.4719 15.0467C37.4531 11.6904 33.7125 9.73105 29.25 9.16855V4.6123C34.95 5.1748 39.75 7.5748 43.65 11.8123C47.55 16.0498 49.5 21.1123 49.5 26.9998C49.5 30.4123 48.8156 33.5623 47.4469 36.4498C46.0781 39.3373 44.2125 41.8123 41.85 43.8748H47.25V48.3748H33.75Z"
                        fill="#FFC600"
                      />
                    </svg>
                    <div className="bcxcontent">
                      <p>
                        Withdrawal <br />
                        transaction
                      </p>
                    </div>
                  </div>
                </div>

                {/* Admin Transactions Metric */}
                <div
                  className="metric transcationbxc"
                  onClick={() => {
                    navigate("/admin-transaction");
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className="bxcontent">
                    <svg
                      width="54"
                      height="54"
                      viewBox="0 0 54 54"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27 49.5C23.8875 49.5 20.9625 48.9094 18.225 47.7281C15.4875 46.5469 13.1062 44.9438 11.0812 42.9188C9.05625 40.8937 7.45312 38.5125 6.27187 35.775C5.09062 33.0375 4.5 30.1125 4.5 27C4.5 23.8875 5.09062 20.9625 6.27187 18.225C7.45312 15.4875 9.05625 13.1062 11.0812 11.0812C13.1062 9.05625 15.4875 7.45312 18.225 6.27187C20.9625 5.09062 23.8875 4.5 27 4.5C29.4375 4.5 31.7437 4.85625 33.9187 5.56875C36.0937 6.28125 38.1 7.275 39.9375 8.55L36.675 11.8687C35.25 10.9687 33.7312 10.2656 32.1187 9.75938C30.5063 9.25313 28.8 9 27 9C22.0125 9 17.7656 10.7531 14.2594 14.2594C10.7531 17.7656 9 22.0125 9 27C9 31.9875 10.7531 36.2344 14.2594 39.7406C17.7656 43.2469 22.0125 45 27 45C31.9875 45 36.2344 43.2469 39.7406 39.7406C43.2469 36.2344 45 31.9875 45 27C45 26.325 44.9625 25.65 44.8875 24.975C44.8125 24.3 44.7 23.6438 44.55 23.0063L48.2062 19.35C48.6187 20.55 48.9375 21.7875 49.1625 23.0625C49.3875 24.3375 49.5 25.65 49.5 27C49.5 30.1125 48.9094 33.0375 47.7281 35.775C46.5469 38.5125 44.9438 40.8937 42.9188 42.9188C40.8937 44.9438 38.5125 46.5469 35.775 47.7281C33.0375 48.9094 30.1125 49.5 27 49.5ZM23.85 37.35L14.2875 27.7875L17.4375 24.6375L23.85 31.05L46.35 8.49375L49.5 11.6437L23.85 37.35Z"
                        fill="#17C470"
                      />
                    </svg>
                    <div className="bcxcontent">
                      <p>
                        Admin <br />
                        Transactions
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ==================================================== */}
          {/* TWO-TAB NAVIGATOR (Carer Requests vs History)        */}
          {/* ==================================================== */}
          <div className="d-flex align-items-center mb-4 mt-2 pb-2 border-bottom flex-wrap gap-2">
            <button
              type="button"
              className={`btn ${
                activeTab === "requests" ? "btn-primary shadow-sm" : "btn-light text-secondary border"
              }`}
              style={{
                borderRadius: "24px",
                padding: "8px 20px",
                fontWeight: "600",
                fontSize: "14px",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
              onClick={() => setActiveTab("requests")}
            >
              <span>🔔 Carer Withdrawal Requests</span>
              {stats.pendingCount > 0 && (
                <span className="badge bg-danger rounded-pill" style={{ fontSize: "11px" }}>
                  {stats.pendingCount} Pending
                </span>
              )}
            </button>

            <button
              type="button"
              className={`btn ${
                activeTab === "history" ? "btn-primary shadow-sm" : "btn-light text-secondary border"
              }`}
              style={{
                borderRadius: "24px",
                padding: "8px 20px",
                fontWeight: "600",
                fontSize: "14px",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
              onClick={() => setActiveTab("history")}
            >
              <span>📋 Withdrawal Transactions History (Original View)</span>
              {list?.count !== undefined && (
                <span className="badge bg-secondary rounded-pill" style={{ fontSize: "11px" }}>
                  {list.count} Total
                </span>
              )}
            </button>
          </div>

          {/* Stripe Environment Indicator Banner */}
          <div
            className="d-flex align-items-center justify-content-between p-2 px-3 mb-3 rounded"
            style={{
              background: stripeEnv?.is_sandbox ? "#fffbeb" : "#f0fdf4",
              border: stripeEnv?.is_sandbox ? "1px solid #fde68a" : "1px solid #bbf7d0",
              color: stripeEnv?.is_sandbox ? "#92400e" : "#166534",
              fontSize: "13px",
            }}
          >
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span style={{ fontSize: "16px" }}>{stripeEnv?.is_sandbox ? "🧪" : "🟢"}</span>
              <span className="fw-bold">Stripe Payment Gateway:</span>
              <Badge
                bg={stripeEnv?.is_sandbox ? "warning" : "success"}
                text={stripeEnv?.is_sandbox ? "dark" : "white"}
                style={{ fontSize: "11px", padding: "4px 8px" }}
              >
                {stripeEnv?.is_sandbox ? "SANDBOX (TEST MODE ACTIVE)" : "LIVE / PRODUCTION MODE"}
              </Badge>
              <span className="text-muted" style={{ fontSize: "12px" }}>
                {stripeEnv?.is_sandbox
                  ? "✓ Safe Mode: Live payments are OFF. All automated transfers and payouts use Stripe Test API."
                  : "⚠️ Live Mode: Real funds will be transferred via Stripe API."}
              </span>
            </div>
          </div>

          {/* ==================================================== */}
          {/* TAB 1: CARER WITHDRAWAL REQUESTS (Approve & Payout)  */}
          {/* ==================================================== */}
          {activeTab === "requests" && (
            <div>
              {/* Filter and Search Bar */}
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${statusFilter === "all" ? "btn-dark" : "btn-outline-secondary"}`}
                    onClick={() => {
                      setStatusFilter("all");
                      setPage(1);
                    }}
                  >
                    All ({stats.totalCount || 0})
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${statusFilter === "pending" ? "btn-warning text-dark fw-bold" : "btn-outline-warning text-dark"}`}
                    onClick={() => {
                      setStatusFilter("pending");
                      setPage(1);
                    }}
                  >
                    Pending ({stats.pendingCount || 0})
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${statusFilter === "completed" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => {
                      setStatusFilter("completed");
                      setPage(1);
                    }}
                  >
                    Completed ({stats.completedCount || 0})
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${statusFilter === "rejected" ? "btn-danger" : "btn-outline-danger"}`}
                    onClick={() => {
                      setStatusFilter("rejected");
                      setPage(1);
                    }}
                  >
                    Rejected ({stats.rejectedCount || 0})
                  </button>
                </div>

                <div style={{ maxWidth: "280px", width: "100%" }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search carer name, email..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>

              {/* Table */}
              <div className="user_list_wrap table-responsive">
                <table className="table table-stripd">
                  <thead>
                    <tr>
                      <th>Carer Details</th>
                      <th>Requested Amount</th>
                      <th>Registered Bank Details</th>
                      <th>Requested Date</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right", paddingRight: "20px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading withdrawal requests...
                        </td>
                      </tr>
                    ) : withdrawals.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          No carer withdrawal requests found for this filter.
                        </td>
                      </tr>
                    ) : (
                      withdrawals.map((item) => {
                        const carerName = `${item.user?.first_name || ""} ${item.user?.last_name || ""}`.trim() || "Unknown Carer";
                        const email = item.user?.email || "No email";
                        const bank = item.user?.bank_details;

                        return (
                          <tr key={item._id}>
                            {/* Carer Details */}
                            <td>
                              <div className="d-flex align-items-center">
                                <div
                                  style={{
                                    width: "38px",
                                    height: "38px",
                                    borderRadius: "50%",
                                    background: "#e0f2fe",
                                    color: "#0369a1",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "700",
                                    fontSize: "14px",
                                    marginRight: "12px",
                                  }}
                                >
                                  {carerName.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontWeight: "600", color: "#111827" }}>{carerName}</div>
                                  <small style={{ color: "#6b7280" }}>{email}</small>
                                </div>
                              </div>
                            </td>

                            {/* Amount */}
                            <td>
                              <div style={{ fontWeight: "700", fontSize: "15px", color: "#111827" }}>
                                ${Number(item.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                <span style={{ fontSize: "11px", fontWeight: "500", marginLeft: "4px", color: "#6b7280" }}>
                                  {(item.currency || "USD").toUpperCase()}
                                </span>
                              </div>
                              <small style={{ color: "#9ca3af" }}>
                                Wallet: ${Number(item.wallet_balance || 0).toFixed(2)}
                              </small>
                            </td>

                            {/* Bank Details */}
                            <td>
                              {bank && (bank.account_number || bank.is_added) ? (
                                <div>
                                  <div style={{ fontWeight: "600", fontSize: "13px", color: "#111827" }}>
                                    {bank.bank_name || "Bank Account"}
                                  </div>
                                  <small style={{ color: "#4b5563" }}>
                                    {bank.account_holder_name ? `${bank.account_holder_name} • ` : ""}
                                    {bank.account_number
                                      ? `****${String(bank.account_number).slice(-4)}`
                                      : "Account Linked"}
                                    {bank.country ? ` (${bank.country.toUpperCase()})` : ""}
                                  </small>
                                  <div className="mt-1 d-flex flex-wrap gap-1">
                                    {item.status === "completed" ? (
                                      item.payout_method === "stripe" ? (
                                        <Badge bg="success" style={{ fontSize: "10px", fontWeight: "500" }}>
                                          ⚡ Paid via Stripe
                                        </Badge>
                                      ) : (
                                        <Badge bg="info" text="dark" style={{ fontSize: "10px", fontWeight: "500" }}>
                                          🏛 Paid Manually {item.reference_number ? `(${item.reference_number})` : ""}
                                        </Badge>
                                      )
                                    ) : item.hasStripeAccount ? (
                                      <Badge bg="primary" style={{ fontSize: "10px", fontWeight: "500" }}>
                                        ⚡ Stripe Ready
                                      </Badge>
                                    ) : (
                                      <Badge bg="secondary" style={{ fontSize: "10px", fontWeight: "500" }}>
                                        🏛 Bank Details Ready
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted" style={{ fontSize: "12px" }}>
                                  No bank details added
                                </span>
                              )}
                            </td>

                            {/* Date */}
                            <td>
                              <div style={{ fontSize: "13px", fontWeight: "500" }}>
                                {moment(item.transactionDate).format("DD MMM YYYY")}
                              </div>
                              <small style={{ color: "#9ca3af" }}>
                                {moment(item.transactionDate).format("hh:mm A")}
                              </small>
                            </td>

                            {/* Status */}
                            <td>
                              {item.status === "pending" && (
                                <Badge bg="warning" text="dark" style={{ padding: "6px 10px", fontSize: "12px" }}>
                                  ⏳ Pending Approval
                                </Badge>
                              )}
                              {item.status === "completed" && (
                                <Badge bg="success" style={{ padding: "6px 10px", fontSize: "12px" }}>
                                  ✓ Paid & Completed
                                </Badge>
                              )}
                              {item.status === "rejected" && (
                                <Badge bg="danger" style={{ padding: "6px 10px", fontSize: "12px" }}>
                                  ✕ Rejected
                                </Badge>
                              )}
                            </td>

                            {/* Actions */}
                            <td style={{ textAlign: "right", paddingRight: "20px" }}>
                              {item.status === "pending" ? (
                                <div className="d-flex justify-content-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="success"
                                    style={{ fontWeight: "600", fontSize: "12px", padding: "4px 12px" }}
                                    onClick={() => handleOpenApprove(item)}
                                  >
                                    Approve Payout
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    style={{ fontWeight: "600", fontSize: "12px", padding: "4px 10px" }}
                                    onClick={() => handleOpenReject(item)}
                                  >
                                    Reject
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  style={{ fontSize: "12px", padding: "4px 10px" }}
                                  onClick={() => handleOpenDetails(item)}
                                >
                                  <i className="fas fa-eye me-1"></i> Details
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-4">
                <Pagination
                  handlePageClick={handlePageClickRequests}
                  pageCount={Math.ceil((totalCount || 0) / limit)}
                />
              </div>
            </div>
          )}

          {/* ==================================================== */}
          {/* TAB 2: ORIGINAL WITHDRAWAL TRANSACTION HISTORY TABLE */}
          {/* ==================================================== */}
          {activeTab === "history" && (
            <div>
              <div className="user_list_wrap table-responsive">
                <table className="table table-stripd">
                  <thead>
                    <tr>
                      <th> Transactions ID </th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list?.transaction?.map(function (object, i) {
                      return (
                        <tr key={object?._id || i}>
                          <td>{object?._id}</td>
                          <td>{moment(object?.createdAt).format("DD-MM-YYYY")}</td>
                          <td>
                            {object?.amount?.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                          </td>
                          <td>
                            <div className="active_button-block">
                              {object?.status}
                            </div>
                          </td>
                          <td>
                            <div
                              className="iconsbcx"
                              onClick={() => {
                                alert("Under Development");
                              }}
                            >
                              <i className="fas fa-eye"></i>
                              <i className="fas fa-trash"></i>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination
                handlePageClick={handlePageClickHistory}
                pageCount={Math.ceil((list?.count || 0) / limit)}
              />
            </div>
          )}
        </div>
      </div>

      {/* APPROVE MODAL (Dual Payout Options) */}
      <Modal show={showApproveModal} onHide={() => !actionLoading && setShowApproveModal(false)} size="lg" centered>
        <Modal.Header closeButton={!actionLoading}>
          <Modal.Title style={{ fontSize: "18px", fontWeight: "700" }}>
            Confirm Withdrawal Payout
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <div>
              {/* Summary Card */}
              <div className="p-3 mb-3" style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                <div className="row g-2">
                  <div className="col-md-6">
                    <span className="text-muted small">Carer Name:</span>
                    <div className="fw-bold">{selectedItem.user?.first_name} {selectedItem.user?.last_name}</div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-muted small">Carer Email:</span>
                    <div>{selectedItem.user?.email || "N/A"}</div>
                  </div>
                  <div className="col-md-6 mt-2">
                    <span className="text-muted small">Requested Amount:</span>
                    <div className="fw-bold text-success" style={{ fontSize: "18px" }}>
                      ${Number(selectedItem.amount || 0).toFixed(2)} {(selectedItem.currency || "USD").toUpperCase()}
                    </div>
                  </div>
                  <div className="col-md-6 mt-2">
                    <span className="text-muted small">Current Wallet Balance:</span>
                    <div className="fw-semibold text-secondary">
                      ${Number(selectedItem.wallet_balance || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Registered Bank Details */}
              <div className="p-3 mb-3" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0 fw-bold" style={{ fontSize: "14px", color: "#1e293b" }}>
                    🏛 Carer's Registered Bank Details
                  </h6>
                  {selectedItem.user?.bank_details && (
                    <Button
                      variant={copied ? "success" : "outline-primary"}
                      size="sm"
                      style={{ fontSize: "12px", padding: "2px 8px" }}
                      onClick={handleCopyBankDetails}
                    >
                      {copied ? "✓ Copied" : "📋 Copy Bank Details"}
                    </Button>
                  )}
                </div>

                {selectedItem.user?.bank_details?.account_number ? (
                  <div className="row g-2 small">
                    <div className="col-6 col-md-4">
                      <span className="text-muted">Bank Name:</span>
                      <div className="fw-semibold">{selectedItem.user.bank_details.bank_name || "N/A"}</div>
                    </div>
                    <div className="col-6 col-md-4">
                      <span className="text-muted">Account Holder:</span>
                      <div className="fw-semibold">{selectedItem.user.bank_details.account_holder_name || "N/A"}</div>
                    </div>
                    <div className="col-6 col-md-4">
                      <span className="text-muted">Account Number:</span>
                      <div className="fw-semibold text-primary">{selectedItem.user.bank_details.account_number || "N/A"}</div>
                    </div>
                    <div className="col-6 col-md-4">
                      <span className="text-muted">Routing / IFSC / Sort:</span>
                      <div className="fw-semibold">{selectedItem.user.bank_details.routing_number || "N/A"}</div>
                    </div>
                    <div className="col-6 col-md-4">
                      <span className="text-muted">SWIFT / BIC:</span>
                      <div className="fw-semibold">{selectedItem.user.bank_details.swift_code || "N/A"}</div>
                    </div>
                    <div className="col-6 col-md-4">
                      <span className="text-muted">Country:</span>
                      <div className="fw-semibold">{(selectedItem.user.bank_details.country || "AU").toUpperCase()}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-danger small">
                    ⚠️ No bank details found for this carer. Carer must add their bank details in the mobile app.
                  </div>
                )}
              </div>

              {/* Payment Gateway Environment Indicator */}
              <div
                className="p-2.5 px-3 mb-3 rounded d-flex align-items-center justify-content-between small"
                style={{
                  background: stripeEnv?.is_sandbox ? "#fffbeb" : "#f0fdf4",
                  border: stripeEnv?.is_sandbox ? "1px solid #fde68a" : "1px solid #bbf7d0",
                  color: stripeEnv?.is_sandbox ? "#92400e" : "#166534",
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: "18px" }}>{stripeEnv?.is_sandbox ? "🧪" : "🟢"}</span>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <strong>Payment Gateway Mode:</strong>
                      <Badge bg={stripeEnv?.is_sandbox ? "warning" : "success"} text={stripeEnv?.is_sandbox ? "dark" : "white"}>
                        {stripeEnv?.is_sandbox ? "SANDBOX / TEST MODE" : "LIVE PRODUCTION"}
                      </Badge>
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                      {stripeEnv?.is_sandbox
                        ? "Safe Mode: No real funds will be debited or paid out. All automated transfers use Stripe Test API."
                        : "Live Mode: Real money will be transferred via Stripe API."}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payout Method Selection */}
              <div className="mb-3">
                <label className="fw-bold mb-2" style={{ fontSize: "14px", color: "#1e293b" }}>
                  Choose Payout Method:
                </label>

                {/* Option 1: Automated Stripe Payout */}
                <div
                  className={`p-3 mb-2 rounded border ${payoutMethod === "stripe" ? "border-primary bg-light" : "border-gray"}`}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.2s",
                    borderWidth: payoutMethod === "stripe" ? "2px" : "1px",
                  }}
                  onClick={() => setPayoutMethod("stripe")}
                >
                  <div className="d-flex align-items-start">
                    <Form.Check
                      type="radio"
                      id="payout-stripe"
                      name="payoutMethod"
                      checked={payoutMethod === "stripe"}
                      onChange={() => setPayoutMethod("stripe")}
                      className="mt-1 me-2"
                    />
                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                        ⚡ Automated Payout via Stripe
                      </div>
                      <div className="text-muted small mt-1">
                        System transfers and triggers automated payout to carer's bank account via Stripe API.
                        Wallet balance is deducted <strong>only after gateway confirmation</strong>.
                      </div>
                      {payoutMethod === "stripe" && (
                        <div className="mt-2 text-success small">
                          ✓ Safe & automated: If the payment gateway fails, wallet balance remains intact.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Option 2: Manual Bank Transfer */}
                <div
                  className={`p-3 rounded border ${payoutMethod === "manual" ? "border-primary bg-light" : "border-gray"}`}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.2s",
                    borderWidth: payoutMethod === "manual" ? "2px" : "1px",
                  }}
                  onClick={() => setPayoutMethod("manual")}
                >
                  <div className="d-flex align-items-start">
                    <Form.Check
                      type="radio"
                      id="payout-manual"
                      name="payoutMethod"
                      checked={payoutMethod === "manual"}
                      onChange={() => setPayoutMethod("manual")}
                      className="mt-1 me-2"
                    />
                    <div style={{ flex: 1 }}>
                      <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                        🏛 Manual Bank Transfer (Netbanking / Wire / NEFT)
                      </div>
                      <div className="text-muted small mt-1">
                        Transfer the funds manually from your company bank portal to the carer's bank details shown above, then enter the UTR / reference number below.
                      </div>

                      {payoutMethod === "manual" && (
                        <div className="mt-3">
                          <Form.Group>
                            <Form.Label className="small fw-semibold text-dark">
                              Bank Transaction / UTR Reference Number <span className="text-danger">*</span>:
                            </Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="e.g. UTR-202610260012 or WIRE-987654"
                              value={referenceNumber}
                              onChange={(e) => setReferenceNumber(e.target.value)}
                              disabled={actionLoading}
                              autoFocus
                            />
                            <Form.Text className="text-muted small">
                              This reference number will be saved on the transaction record for carer audit.
                            </Form.Text>
                          </Form.Group>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" disabled={actionLoading} onClick={() => setShowApproveModal(false)}>
            Cancel
          </Button>
          <Button
            variant={payoutMethod === "stripe" ? "success" : "primary"}
            size="sm"
            disabled={actionLoading}
            onClick={handleConfirmApprove}
            style={{ minWidth: "180px" }}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {payoutMethod === "stripe" ? "Processing via Stripe..." : "Approving..."}
              </>
            ) : payoutMethod === "stripe" ? (
              "⚡ Confirm & Payout via Stripe"
            ) : (
              "🏛 Confirm Manual Transfer"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* REJECT MODAL */}
      <Modal show={showRejectModal} onHide={() => !actionLoading && setShowRejectModal(false)} centered>
        <Modal.Header closeButton={!actionLoading}>
          <Modal.Title style={{ fontSize: "18px", fontWeight: "700" }}>
            Reject Withdrawal Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <div>
              <p style={{ fontSize: "14px" }}>
                Are you sure you want to reject the withdrawal request of{" "}
                <strong>
                  ${Number(selectedItem.amount || 0).toFixed(2)} {selectedItem.currency}
                </strong>{" "}
                for <strong>{selectedItem.user?.first_name} {selectedItem.user?.last_name}</strong>?
              </p>
              <p className="text-muted" style={{ fontSize: "12px" }}>
                Rejecting this request will immediately restore the carer's available wallet balance.
              </p>
              <Form.Group className="mt-3">
                <Form.Label style={{ fontWeight: "600", fontSize: "13px" }}>
                  Reason for Rejection:
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="e.g., Bank details incorrect, pending trip discrepancy..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" disabled={actionLoading} onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={actionLoading}
            onClick={handleConfirmReject}
          >
            {actionLoading ? <Spinner animation="border" size="sm" /> : "Confirm Rejection"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DETAILS MODAL */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "18px", fontWeight: "700" }}>
            Withdrawal Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <div style={{ fontSize: "13px" }}>
              <div className="mb-2"><strong>Transaction ID:</strong> {selectedItem._id}</div>
              <div className="mb-2"><strong>Carer:</strong> {selectedItem.user?.first_name} {selectedItem.user?.last_name} ({selectedItem.user?.email})</div>
              <div className="mb-2"><strong>Amount:</strong> ${Number(selectedItem.amount || 0).toFixed(2)} {(selectedItem.currency || "USD").toUpperCase()}</div>
              <div className="mb-2"><strong>Status:</strong> {selectedItem.status}</div>
              {selectedItem.payout_method && (
                <div className="mb-2">
                  <strong>Payout Method:</strong>{" "}
                  {selectedItem.payout_method === "stripe" ? "⚡ Automated Stripe Payout" : "🏛 Manual Bank Transfer"}
                </div>
              )}
              {selectedItem.reference_number && (
                <div className="mb-2"><strong>Reference / UTR:</strong> {selectedItem.reference_number}</div>
              )}
              <div className="mb-2"><strong>Requested Date:</strong> {moment(selectedItem.transactionDate).format("DD MMM YYYY, hh:mm A")}</div>
              {selectedItem.stripe_transfer_id && (
                <div className="mb-2 text-success">
                  <strong>Stripe Transfer ID:</strong> {selectedItem.stripe_transfer_id}
                </div>
              )}
              {selectedItem.stripe_payout_id && (
                <div className="mb-2 text-success">
                  <strong>Stripe Payout ID:</strong> {selectedItem.stripe_payout_id}
                </div>
              )}
              {selectedItem.rejection_reason && (
                <div className="mb-2 text-danger">
                  <strong>Rejection Reason:</strong> {selectedItem.rejection_reason}
                </div>
              )}
              {selectedItem.processed_at && (
                <div className="mb-2"><strong>Processed At:</strong> {moment(selectedItem.processed_at).format("DD MMM YYYY, hh:mm A")}</div>
              )}
              <hr />
              <div className="fw-bold mb-1">Bank Information:</div>
              <div>Bank Name: {selectedItem.user?.bank_details?.bank_name || "N/A"}</div>
              <div>Account Holder: {selectedItem.user?.bank_details?.account_holder_name || "N/A"}</div>
              <div>Account Number: {selectedItem.user?.bank_details?.account_number || "N/A"}</div>
              <div>Routing / Sort Code: {selectedItem.user?.bank_details?.routing_number || "N/A"}</div>
              <div>SWIFT / BIC: {selectedItem.user?.bank_details?.swift_code || "N/A"}</div>
              <div>Country: {selectedItem.user?.bank_details?.country || "N/A"}</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}
